import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import type { BookingInvitationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** The group-respond payload — which recurring group, and the accept/decline
 *  decision. No uid: the server scopes to the token user (no IDOR surface). */
export interface RespondBookingInvitationGroupVars {
  recurringGroupId: string;
  accept: boolean;
}

/**
 * Accept or decline a WHOLE recurring booking group in one tap — the mutation
 * behind a GROUPED BookingInvitesInboxPage card. Accept → POST /users/me/booking-
 * invitations/accept-group (adds the caller to EVERY slot's roster); decline →
 * .../decline-group. The body carries ONLY the recurring_group_id.
 *
 * On SUCCESS it invalidates the cache slots a group-respond can change so each
 * re-reads the server's truth — never an optimistic local edit:
 *   - booking.myInvitations()  — the inbox: the whole group leaves the pending list
 *                                (both accept AND decline).
 * On ACCEPT it also invalidates:
 *   - booking participants     — adds the caller to EVERY slot's roster;
 *                                the group spans many bookings, so invalidate the
 *                                whole `["booking","participants"]` prefix.
 *   - booking detail           — each accepted booking-detail hub reflects the new
 *                                participant / state; invalidate the `["booking",
 *                                "detail"]` prefix.
 *   - both My bookings reads   — every facility-scoped list plus the cross-
 *                                facility list now includes the accepted set.
 *   - Play next-up             — the home/Play summary cannot retain the
 *                                pre-accept result.
 * It does NOT swallow errors — a 409 / 404 / 403 surfaces on `mutation.error` for
 * the page to map to honest inline copy (same status-keyed map as the single path).
 */
export function useRespondBookingInvitationGroup() {
  const queryClient = useQueryClient();

  return useMutation<
    BookingInvitationResponse[],
    unknown,
    RespondBookingInvitationGroupVars
  >({
    mutationFn: ({
      recurringGroupId,
      accept,
    }: RespondBookingInvitationGroupVars) =>
      accept
        ? userApi.acceptBookingInvitationGroup({
            recurring_group_id: recurringGroupId,
          })
        : userApi.declineBookingInvitationGroup({
            recurring_group_id: recurringGroupId,
          }),
    onSuccess: (_data, vars) => {
      // The inbox refetches so the whole group leaves the pending list.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.booking.myInvitations(),
      });
      if (!vars.accept) return;

      // An accept adds the caller to EVERY slot's roster — the group spans many
      // bookings, so invalidate the whole participants prefix (React Query matches
      // by key prefix) rather than one (facility, booking).
      void queryClient.invalidateQueries({
        queryKey: ["booking", "participants"],
      });
      // Each accepted booking-detail hub reflects the new participant / state.
      void queryClient.invalidateQueries({
        queryKey: ["booking", "detail"],
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.booking.myBookingsPrefix(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.booking.allBookings(),
      });
      // Play is a separate cache consumer of the same participant-visible list.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.play.myUpcoming(),
      });
    },
  });
}
